import React from 'react';
import {FormikErrors, withFormik} from 'formik';
import PropTypes from 'prop-types';
import {PersonalDataServiceRequest} from 'commons/models';
import {InferPropTypes} from 'commons/types/Types';
import InnerFormFramework from './InnerFormDocsInfo';
import {FieldShape} from 'commons/shapes';
import {
    formValidator,
    isFutureDate,
    validateDateonMoralRFC,
    validateDateonRFCandCURP,
} from 'commons/utils/Functions';
import {Messages} from 'commons/constants';

DocsInfoForm.propTypes = {
    rfc: PropTypes.string.isRequired,
    foreign: PropTypes.bool.isRequired,
    submitHandler: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(PropTypes.shape(FieldShape).isRequired).isRequired,
    personalData: PropTypes.any,
    renapoData: PropTypes.any,
    catBanks: PropTypes.any,
};

const FormControl = withFormik<
    InferPropTypes<typeof DocsInfoForm.propTypes>,
    PersonalDataServiceRequest
>({
    mapPropsToErrors: () => {
        return {
            expedientId: '',
            workflowId: '',
            viewId: '',
            viewFatherId: '',
            kycId: '',
            personTypeKey: '',
            rfc: '',
            businessDescription: '',
            constitutionDate: '',
            nationalityKey: '',
            businessLineKey: '',
            publicDeedNumber: '',
            protocolizationDate: '',
            role: '',
            advancedElectronicSignature: '',
            economicActivity: '',
            name: '',
            lastName: '',
            secondLastName: '',
            curp: '',
            birthDate: '',
            countryOrigin: '',
            birthState: '',
            gender: '',
            occupationDescription: '',
            nif: '',
            bank: '',
            clabe: '',
            declarativePep: '',
            mainIncomeSource: '',
            countryIssuingNif: ''
        };
    },
    mapPropsToValues: (props) => {
        const {personalData} = props;
        if (personalData) {
            return {
                expedientId: 0,
                workflowId: 0,
                viewId: 0,
                viewFatherId: 0,
                kycId: 0,
                personTypeKey: '',
                rfc: personalData.rfc || props.rfc,
                businessDescription: personalData.businessDescription || '',
                constitutionDate: personalData.constitutionDate || null,
                nationalityKey: personalData.nationalityKey || '',
                businessLineKey: personalData.businessLineKey || '',
                publicDeedNumber: personalData.publicDeedNumber || '',
                protocolizationDate: personalData.protocolizationDate || null,
                role: '',
                advancedElectronicSignature: personalData.advancedElectronicSignature || '',
                economicActivity: personalData.economicActivity || '',
                name: personalData.name || '',
                lastName: personalData.lastName || '',
                secondLastName: personalData.secondLastName || '',
                curp: personalData.curp || '',
                birthDate: personalData.birthDate || null,
                countryOrigin: personalData.countryOrigin || '',
                birthState: personalData.birthState || '',
                gender: personalData.gender || '',
                occupationDescription: personalData.occupationDescription || '',
                nif: personalData.nif || '',
                validationResult: true,
                bank: personalData.bank,
                clabe: personalData.clabe,
                declarativePep: personalData.declarativePep,
                mainIncomeSource: personalData.mainIncomeSource,
                economicActivityDetail: personalData.economicActivityDetail,
                countryIssuingNif: personalData.countryIssuingNif
            };
        } else {
            return {
                expedientId: 0,
                workflowId: 0,
                viewId: 0,
                viewFatherId: 0,
                kycId: 0,
                personTypeKey: '',
                rfc: props.rfc,
                businessDescription: '',
                constitutionDate: null,
                nationalityKey: '',
                businessLineKey: '',
                publicDeedNumber: '',
                protocolizationDate: null,
                role: '',
                advancedElectronicSignature: '',
                economicActivity: '',
                name: '',
                lastName: '',
                secondLastName: '',
                curp: '',
                birthDate: null,
                countryOrigin: '',
                birthState: '',
                gender: '',
                occupationDescription: '',
                nif: '',
                validationResult: false,
                clabe: '',
                bank: '',
                declarativePep: '',
                mainIncomeSource: '',
                economicActivityDetail: '',
                countryIssuingNif: ''
            };
        }
    },
    validateOnBlur: true,
    validateOnChange: true,
    validate: (values: PersonalDataServiceRequest, props) => {
        const errors: FormikErrors<PersonalDataServiceRequest> = {};
        const renapoData = props.renapoData;
        formValidator(props.fields, errors, values);
        if (values.rfc !== props.rfc) {
            errors.rfc = Messages.RFC_NOT_MATCH_WITH_USERNAME;
        }
        if (!props.foreign) {
            if (values.curp && values.birthDate) {
                if (!validateDateonRFCandCURP(values.birthDate, values.curp))
                    errors.curp = Messages.CURP_NOT_MATCH_WITH_DBO;
            }
            if (values.rfc && values.birthDate) {
                if (!validateDateonRFCandCURP(values.birthDate, values.rfc))
                    errors.rfc = Messages.RFC_NOT_MATCH_WITH_DBO;
            }

            if (values.rfc && values.constitutionDate) {
                if (!validateDateonMoralRFC(values.constitutionDate, values.rfc))
                    errors.rfc = Messages.RFC_NOT_MATCH_WITH_CONSTITUION_DATE;
                if (isFutureDate(values.constitutionDate)) {
                    errors.constitutionDate = Messages.INVALID_FUTURE_DATE;
                }
            }

            if (values.curp && !values.validationResult) {
                errors.curp = Messages.CURP_VALIDATION_REQUIRED;
            }
        }

        if (values.protocolizationDate) {
            if (isFutureDate(values.protocolizationDate)) {
                errors.protocolizationDate = Messages.INVALID_FUTURE_DATE;
            }
        }

        if (values.name && renapoData && renapoData.nombre && values.name !== renapoData.nombre) {
            errors.name = `${Messages.INVALID_VALUE_VS_RENAPO} con el nombre obtenido de RENAPO`;
        }

        if (
            values.lastName &&
            renapoData &&
            renapoData.apellidoPaterno &&
            values.lastName !== renapoData.apellidoPaterno
        ) {
            errors.lastName = `${Messages.INVALID_VALUE_VS_RENAPO} con el apellido paterno obtenido de RENAPO`;
        }

        if (
            values.secondLastName &&
            renapoData &&
            renapoData.apellidoMaterno &&
            values.secondLastName !== renapoData.apellidoMaterno
        ) {
            errors.secondLastName = `${Messages.INVALID_VALUE_VS_RENAPO} con el apellido materno obtenido de RENAPO`;
        }
        return errors;
    },
    handleSubmit: (values, actions) => {
        actions.setSubmitting(false);
        actions.props.submitHandler({...values});
    },
})(InnerFormFramework);

function DocsInfoForm(props: InferPropTypes<typeof DocsInfoForm.propTypes>) {
    const {submitHandler, fields, personalData, foreign, renapoData, catBanks, rfc} = props;

    /* eslint-disable react/jsx-handler-names */
    return (
        <FormControl
            rfc={rfc}
            foreign={foreign}
            fields={fields}
            submitHandler={submitHandler}
            personalData={personalData}
            renapoData={renapoData}
            catBanks={catBanks}
        />
    );
}

export default DocsInfoForm;
